/**
 * Decision 0166 §3, 0167: Settings › Subscriptions (by account) and Account › My subscriptions.
 *   GET  /api/workspaces/:ws/subscriptions                                → SubscriptionsOverview
 *   PUT  /api/workspaces/:ws/subscriptions/accounts/:key/sharing {scope, projectIds?}  (the account's owner, 0167 §4)
 *   PATCH /api/workspaces/:ws/subscriptions/accounts/:key {label}                     (the account's owner)
 *   PUT  /api/workspaces/:ws/projects/:projectId/subscription-choice {keys} (the viewer's own choice, §3.6)
 *   PATCH /api/workspaces/:ws/platform-runner/subscriptions/:id {accountEmail}        (owner)
 *   GET  /api/me/subscriptions                                            → MySubscriptionsView (0167 §3)
 */
import { MySubscriptionsView, type ProfileScope, SubscriptionsOverview } from "@minga/protocol"
import { queryOptions } from "@tanstack/react-query"
import { apiJson, apiRequest } from "@/lib/api"

const enc = encodeURIComponent
const ws = (workspaceId: string) => `/api/workspaces/${enc(workspaceId)}`

export const subscriptionsQuery = (workspaceId: string) =>
  queryOptions({
    queryKey: ["workspaces", workspaceId, "subscriptions"],
    queryFn: () => apiJson(`${ws(workspaceId)}/subscriptions`, SubscriptionsOverview),
    staleTime: 15_000,
  })

/** Decision 0167 §3: the viewer's own accounts across every workspace they are in. */
export const mySubscriptionsQuery = queryOptions({
  queryKey: ["me", "subscriptions"],
  queryFn: () => apiJson("/api/me/subscriptions", MySubscriptionsView),
  staleTime: 15_000,
})

/** Decision 0166 §3.6: which subscriptions the viewer's work in this project uses (null: everything they may use). */
export const setSubscriptionChoice = (
  workspaceId: string,
  projectId: string,
  keys: ReadonlyArray<string> | null,
) =>
  apiRequest(`${ws(workspaceId)}/projects/${enc(projectId)}/subscription-choice`, null, {
    method: "PUT",
    body: { keys },
  })

/** Decision 0167 §4: where the owner shares one of their accounts, for every connection of it at once. */
export const setAccountSharing = (
  workspaceId: string,
  key: string,
  scope: ProfileScope,
  projectIds: ReadonlyArray<string>,
) =>
  apiRequest(`${ws(workspaceId)}/subscriptions/accounts/${enc(key)}/sharing`, null, {
    method: "PUT",
    body: scope === "projects" ? { scope, projectIds } : { scope },
  })

/** Decision 0166 §3.3, 0167 §1: the owner's name for one of their accounts (null clears it). */
export const setAccountLabel = (workspaceId: string, key: string, label: string | null) =>
  apiRequest(`${ws(workspaceId)}/subscriptions/accounts/${enc(key)}`, null, {
    method: "PATCH",
    body: { label },
  })

/** Decision 0166 §3.4: the owner types a Platform subscription's account email (Claude, until a sandbox reports it). */
export const setEntryAccountEmail = (workspaceId: string, id: string, accountEmail: string | null) =>
  apiRequest(`${ws(workspaceId)}/platform-runner/subscriptions/${enc(id)}`, null, {
    method: "PATCH",
    body: { accountEmail },
  })
